/**
 * Organization Service
 *
 * API service for organization-related operations.
 */

import { apiClient } from '../axios';
import type { ApiResponse } from '../axios';
import { API_ENDPOINTS } from '../endpoints';
import { ApiError } from './user.service';

interface RawApiResponse<T> {
  status: string;
  statusCode: number;
  statusMessage?: string | null;
  showMessage?: string;
  result: T;
}

function throwIfFailed(data: RawApiResponse<unknown>, fallbackMessage: string): void {
  if (data.status === 'fail' || data.status === 'error') {
    throw new ApiError(
      data.statusCode,
      data.showMessage || data.statusMessage || fallbackMessage
    );
  }
}

import type {
  ListRequest,
  OrganizationListResponse,
  OrganizationDetails,
  OrganizationUsersResponse,
  OrganizationPlan,
  PendingRegistrationListResponse,
  LoginAuditOrganizationResponse,
  LoginAuditHistoryResponse,
  LoginAuditHistoryApiResponse,
  OTPVerificationRequest,
  OTPVerificationResponse,
  CreateOrganizationRequest,
  SendSmsOtpRequest,
  VerifySmsOtpRequest,
  SendEmailOtpRequest,
  VerifyEmailOtpRequest,
} from '../types';

// ============================================
// Organization List & Details
// ============================================

/**
 * Get list of organizations with pagination
 */
export async function getOrganizationList(
  params: ListRequest
): Promise<OrganizationListResponse> {
  const response = await apiClient.post<ApiResponse<OrganizationListResponse>>(
    API_ENDPOINTS.ORGANIZATION.LIST,
    params
  );
  return response.data.result;
}

/**
 * Get organization by ID
 */
export async function getOrganizationById(id: string): Promise<OrganizationDetails> {
  const response = await apiClient.get<ApiResponse<OrganizationDetails>>(
    API_ENDPOINTS.ORGANIZATION.GET_BY_ID(id)
  );
  return response.data.result;
}

/**
 * Create a new organization
 */
export async function createOrganization(
  data: CreateOrganizationRequest
): Promise<void> {
  const response = await apiClient.post<RawApiResponse<unknown>>(
    API_ENDPOINTS.ORGANIZATION.CREATE,
    data
  );
  throwIfFailed(response.data, 'Failed to create organization');
}

// ============================================
// Organization Users
// ============================================

/**
 * DataTables-style request for organization users
 */
interface OrgUsersRequest {
  orgId: string;
  draw: number;
  start: number;
  length: number;
  search: {
    value: string;
    regex: boolean;
  };
  columns: Array<{
    data: string | null;
    name: string;
    searchable: boolean;
    orderable: boolean;
    search: { value: string; regex: boolean };
  }>;
  order: Array<{ column: number; dir: 'asc' | 'desc' }>;
  x?: string;
}

/**
 * Get users for a specific organization
 * Uses DataTables-style API format
 */
export async function getOrganizationUsers(
  orgId: string,
  params: { page: number; size: number; search?: string }
): Promise<OrganizationUsersResponse> {
  const request: OrgUsersRequest = {
    orgId,
    draw: params.page + 1,
    start: params.page * params.size,
    length: params.size,
    search: {
      value: params.search || '',
      regex: false,
    },
    columns: [
      { data: 'profileDocumentId', name: '', searchable: true, orderable: false, search: { value: '', regex: false } },
      { data: 'username', name: '', searchable: true, orderable: false, search: { value: '', regex: false } },
      { data: null, name: '', searchable: true, orderable: false, search: { value: '', regex: false } },
      { data: 'mobile', name: '', searchable: true, orderable: false, search: { value: '', regex: false } },
      { data: 'email', name: '', searchable: true, orderable: false, search: { value: '', regex: false } },
      { data: 'createdDate', name: '', searchable: true, orderable: false, search: { value: '', regex: false } },
      { data: 'status', name: '', searchable: true, orderable: false, search: { value: '', regex: false } },
      { data: 'id', name: '', searchable: true, orderable: false, search: { value: '', regex: false } },
    ],
    order: [],
    x: '121',
  };

  const response = await apiClient.post<ApiResponse<OrganizationUsersResponse>>(
    API_ENDPOINTS.ORGANIZATION.USERS,
    request
  );
  return response.data.result;
}

// ============================================
// Organization Plan
// ============================================

/**
 * Get organization's subscription plan details
 */
export async function getOrganizationPlan(orgId: string): Promise<OrganizationPlan[]> {
  const response = await apiClient.get<ApiResponse<OrganizationPlan[]>>(
    API_ENDPOINTS.SUBSCRIPTION.GET_ORG_PLAN(orgId)
  );
  return response.data.result;
}

// ============================================
// Pending Registrations
// ============================================

/**
 * Get list of pending organization registrations
 */
export async function getPendingRegistrationList(
  params: ListRequest
): Promise<PendingRegistrationListResponse> {
  const response = await apiClient.post<ApiResponse<PendingRegistrationListResponse>>(
    API_ENDPOINTS.ORGANIZATION.PENDING_LIST,
    params
  );
  return response.data.result;
}

// ============================================
// Login Audit
// ============================================

/**
 * Get login audit organization grid
 */
export async function getLoginAuditOrganizationGrid(
  params: ListRequest
): Promise<LoginAuditOrganizationResponse> {
  const response = await apiClient.post<ApiResponse<LoginAuditOrganizationResponse>>(
    API_ENDPOINTS.AUDIT.LOGIN_GRID,
    params
  );
  return response.data.result;
}

/**
 * Get login audit history for a specific organization
 */
export async function getLoginAuditHistory(
  orgId: string,
  params: ListRequest
): Promise<LoginAuditHistoryResponse> {
  const response = await apiClient.post<ApiResponse<LoginAuditHistoryApiResponse>>(
    API_ENDPOINTS.AUDIT.HISTORY,
    {
      orgId,
      ...params,
    }
  );

  const result = response.data.result;

  // Transform API response to UI-friendly format
  return {
    list: result.loginUserAuditList.map(item => ({
      id: item.id,
      userId: item.userId,
      username: item.userName,
      loginTime: item.loginTime,
      logoutTime: item.logoutTime || undefined,
      ipAddress: item.ipAddress,
      deviceType: item.deviceType,
      sessionId: item.sessionId || undefined,
    })),
    totalRecords: result.totalRecords,
    filteredRecords: result.filteredRecords,
  };
}

// ============================================
// OTP Verification
// ============================================

/**
 * Verify OTP for support session
 */
export async function verifySupportSessionOTP(
  data: OTPVerificationRequest
): Promise<OTPVerificationResponse> {
  const response = await apiClient.post<ApiResponse<OTPVerificationResponse>>(
    API_ENDPOINTS.ORGANIZATION.VERIFY_OTP,
    data
  );
  return response.data.result;
}

// ============================================
// Registration OTP Verification
// ============================================

/**
 * Send SMS OTP for mobile verification
 */
export async function sendSmsOtp(
  data: SendSmsOtpRequest
): Promise<void> {
  const response = await apiClient.post<RawApiResponse<unknown>>(
    API_ENDPOINTS.ORG_REGISTRATION.SEND_SMS_OTP,
    data
  );
  throwIfFailed(response.data, 'Failed to send SMS OTP');
}

/**
 * Verify SMS OTP
 */
export async function verifySmsOtp(
  data: VerifySmsOtpRequest
): Promise<void> {
  const response = await apiClient.post<RawApiResponse<unknown>>(
    API_ENDPOINTS.ORG_REGISTRATION.VERIFY_SMS_OTP,
    data
  );
  throwIfFailed(response.data, 'SMS OTP verification failed');
}

/**
 * Send email OTP for email verification
 */
export async function sendEmailOtp(
  data: SendEmailOtpRequest
): Promise<void> {
  const response = await apiClient.post<RawApiResponse<unknown>>(
    API_ENDPOINTS.ORG_REGISTRATION.SEND_EMAIL_OTP,
    data
  );
  throwIfFailed(response.data, 'Failed to send email OTP');
}

/**
 * Verify email OTP
 */
export async function verifyEmailOtp(
  data: VerifyEmailOtpRequest
): Promise<void> {
  const response = await apiClient.post<RawApiResponse<unknown>>(
    API_ENDPOINTS.ORG_REGISTRATION.VERIFY_EMAIL,
    data
  );
  throwIfFailed(response.data, 'Email OTP verification failed');
}

// ============================================
// Allowed Plans for Platform User
// ============================================

/**
 * Get allowed subscription plans for a platform user
 */
export async function getAllowedPlansByUserId(
  userId: string
): Promise<any[]> {
  const response = await apiClient.get<RawApiResponse<any[]>>(
    API_ENDPOINTS.ORG_REGISTRATION.ALLOWED_PLANS_BY_USER(userId)
  );
  throwIfFailed(response.data, 'Failed to fetch allowed plans');
  return response.data.result || [];
}

/**
 * Get allowed plan details (with terms) for a platform user
 */
export async function getAllowedPlanDetailsByUserId(
  userId: string
): Promise<any[]> {
  const response = await apiClient.get<RawApiResponse<any[]>>(
    API_ENDPOINTS.ORG_REGISTRATION.ALLOWED_PLAN_DETAILS_BY_USER(userId)
  );
  throwIfFailed(response.data, 'Failed to fetch allowed plan details');
  return response.data.result || [];
}

/**
 * Assign allowed plans to a platform user
 */
export async function createAllowedPlansForUser(
  data: { allowedPlanCodeList: string[]; removePlanCodeList: string[]; platromUserId: string }
): Promise<void> {
  const response = await apiClient.post<RawApiResponse<unknown>>(
    API_ENDPOINTS.ORG_REGISTRATION.CREATE_ALLOWED_PLANS,
    data
  );
  throwIfFailed(response.data, 'Failed to assign plans');
}

// ============================================
// Export all functions
// ============================================

export const organizationService = {
  getOrganizationList,
  getOrganizationById,
  createOrganization,
  getOrganizationUsers,
  getOrganizationPlan,
  getPendingRegistrationList,
  getLoginAuditOrganizationGrid,
  getLoginAuditHistory,
  verifySupportSessionOTP,
  sendSmsOtp,
  verifySmsOtp,
  sendEmailOtp,
  verifyEmailOtp,
  getAllowedPlansByUserId,
  getAllowedPlanDetailsByUserId,
  createAllowedPlansForUser,
};
